import type {
  ClientMenuItem,
  Filter,
  NavigationItem,
} from '@dealerportal/common';
import type { CSSProperties } from 'react';
import slugify from 'slugify';
import type { TokenValue } from './TokenReplace';

export function TokenHelper(tokens: TokenValue[]) {
  return {
    get(tokenName: string): string | undefined {
      return tokens?.find(({ TokenName }) => TokenName === tokenName)
        ?.Replacement;
    },

    has(tokenName: string): boolean {
      return tokens?.some(({ TokenName }) => TokenName === tokenName);
    },
  };
}

export function utilities(): string {
  return 'utilities';
}

export function hexToRgba(hex: string, alpha = 1): string | null {
  hex = hex.replace('#', '').toUpperCase();

  if (hex.length !== 3 && hex.length !== 6) {
    return null;
  }

  if (!/^[0-9A-F]+$/i.test(hex)) {
    return null;
  }

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function normalizeHexColor(color?: string | null): string | null {
  if (color == null) return null;
  const raw = color.trim();
  if (!raw || /^default$/i.test(raw)) return null;

  const hex = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hex?.[1]) {
    let value = hex[1];
    if (value.length === 3) {
      value = value
        .split('')
        .map((character) => character + character)
        .join('');
    } else if (value.length === 8) {
      value = value.slice(0, 6);
    }
    return `#${value.toLowerCase()}`;
  }

  const rgb = raw.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)$/i
  );
  const csv = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
  const channels = (rgb ?? csv)?.slice(1, 4);
  if (channels) {
    const values = channels.map((channel) =>
      Math.min(255, Number.parseInt(channel, 10))
    );
    return `#${values.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
  }

  return null;
}

export function removeHtmlTags(text: string, addColonToHeadings = true) {
  if (text === null || text === undefined) return null;

  if (addColonToHeadings) {
    // Adds a : after each heading tag
    text = text.replace(/<\/h[1-6]>/gi, ': ');
  }

  // Removes all other tags and adds a space
  const parts: string[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const tagStart = text.indexOf('<', cursor);
    if (tagStart === -1) break;
    const tagEnd = text.indexOf('>', tagStart + 1);
    if (tagEnd === -1) break;

    if (tagEnd === tagStart + 1) {
      parts.push(text.slice(cursor, tagEnd + 1));
    } else {
      parts.push(text.slice(cursor, tagStart), ' ');
    }
    cursor = tagEnd + 1;
  }
  parts.push(text.slice(cursor));
  text = parts.join('');
  // Removes all double spaces
  text = text.replace(/\s+/g, ' ');

  if (addColonToHeadings) {
    // Removes leading and trailing spaces before colons
    text = text.replace(/\s+:/g, ': ');
  }

  return text.trim();
}

export function getReadingTime(text: string) {
  const wpm = 225;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wpm);
}

export function isAllCaps(string: string) {
  if (!string) return false;
  return string === string.toUpperCase();
}

export function toKebab(string: string, strict: boolean = false) {
  const inputString = isAllCaps(string) ? string.toLowerCase() : string;

  let result = inputString
    .split('')
    .map((letter) => {
      if (/[A-Z]/.test(letter)) {
        return ` ${letter.toLowerCase()}`;
      }
      return letter;
    })
    .join('')
    .trim()
    .replace(/[_\s]+/g, '-');

  if (strict) {
    result = result.replace(/[^a-z0-9-]/gi, '');
    result = result.replace(/-+/g, '-');
    result = result.replace(/^-|-$/g, '');
  }

  return result;
}

export function toTitleCase(string: string) {
  const titleCaseExceptions = [
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'from',
    'if',
    'in',
    'nor',
    'per',
    'of',
    'on',
    'or',
    'so',
    'the',
    'to',
    'up',
    'yet',
  ];

  return toKebab(string)
    .split('-')
    .map((word, index) => {
      if (titleCaseExceptions.includes(word) && index !== 0) return word;

      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export function toTitleCaseWithUnderscores(input: string): string {
  return input
    ?.split('_')
    ?.map(
      (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()
    )
    ?.join('_');
}

export function upperWords(string: string) {
  return toKebab(string)
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function toPascalCase(string: string) {
  return upperWords(string).replaceAll(' ', '');
}

export function styleToObject(string: string | undefined) {
  if (!string) return {};

  const cssRules: { [key: string]: string } = {};

  string.split(';').forEach((declaration) => {
    const key = declaration.split(':')[0].trim();
    const val = declaration.split(':')[1].trim();

    if (!key || !val) return;

    cssRules[key] = val;
  });

  return cssRules as CSSProperties;
}

export function isValidURL(urlString: string) {
  let url: URL;

  try {
    url = new URL(urlString);
  } catch {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export function isValidQueryString(queryString: string) {
  queryString = queryString.startsWith('?') ? queryString : `?${queryString}`;
  return /^[?][^=]+=[^&]+(&[^=]+=[^&]+)*$/.test(queryString);
}

export function isFalsyString(string: string | undefined): boolean {
  if (!string) return true;

  const falsyValues = [
    '',
    '0',
    'nan',
    'null',
    'none',
    'void',
    'false',
    'undefined',
  ];

  return falsyValues.includes(string.toLowerCase());
}

export function isTruthyString(string: string | undefined): boolean {
  return !isFalsyString(string);
}

export function isJSON(string: string | undefined): boolean {
  if (isFalsyString(string)) return false;

  try {
    JSON.parse(string || '');

    return true;
  } catch {
    return false;
  }
}

/**
 *
 * Hash a string into a number
 * Do not use this for anything security related, chances of collision are high
 * @param str the string to hash
 * @returns a unique hash for the string
 */
export function HashStringInsecure(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
}

export enum BroadstreetZoneType {
  Carpet = 67073,
  Hardwood = 67074,
  Laminate = 67075,
  Tile = 67076,
  Waterproof = 67079,
  LuxuryVinyl = 67080,
  Vinyl = 67081,
  NaturalStone = 67082,
  GlassTile = 67118,
  Cork = 67119,
  MetalTile = 67120,
  AreaRugs = 67121,
  CarpetTile = 76906,
  SolidSurface = 78493,
  Default = 64661,
  PXMVideo = 90164,
}

export const getProductTypeSlugAndZone = (
  hasOldSteup: boolean,
  productType: string
) => {
  let oldSlug: string;
  let slug: string;
  let zone: number;

  switch (productType) {
    case 'area-rugs':
      oldSlug = '|all-area-rugs-flooring';
      slug = 'area';
      zone = BroadstreetZoneType.AreaRugs;
      break;
    case 'carpet':
      oldSlug = '|all-carpet-flooring';
      slug = 'carpet';
      zone = BroadstreetZoneType.Carpet;
      break;
    case 'carpet-tile':
      oldSlug = '|all-carpet-tile-flooring';
      slug = 'carpetTile';
      zone = BroadstreetZoneType.CarpetTile;
      break;
    case 'cork':
      oldSlug = '|all-cork-flooring';
      slug = 'cork';
      zone = BroadstreetZoneType.Cork;
      break;
    case 'glass-tile':
      oldSlug = '|all-glass-tile-flooring';
      slug = 'glass';
      zone = BroadstreetZoneType.GlassTile;
      break;
    case 'hardwood':
      oldSlug = '|all-hardwood-flooring';
      slug = 'hardwood';
      zone = BroadstreetZoneType.Hardwood;
      break;
    case 'laminate':
      oldSlug = '|all-laminate-flooring';
      slug = 'laminate';
      zone = BroadstreetZoneType.Laminate;
      break;
    case 'luxury-vinyl':
      oldSlug = '|all-luxury-vinyl-flooring';
      slug = 'lvt';
      zone = BroadstreetZoneType.LuxuryVinyl;
      break;
    case 'metal-tile':
      oldSlug = '|all-metal-tile-flooring';
      slug = 'metal';
      zone = BroadstreetZoneType.MetalTile;
      break;
    case 'natural-stone':
      oldSlug = '|all-natural-stone-floor-tile';
      slug = 'stone';
      zone = BroadstreetZoneType.NaturalStone;
      break;
    case 'solid-surface':
      oldSlug = '|all-solid-surfaces';
      slug = 'solidsurface';
      zone = BroadstreetZoneType.SolidSurface;
      break;
    case 'tile':
      oldSlug = '|all-tile-flooring';
      slug = 'tile';
      zone = BroadstreetZoneType.Tile;
      break;
    case 'vinyl':
      oldSlug = '|all-vinyl-flooring';
      slug = 'vinyl';
      zone = BroadstreetZoneType.Vinyl;
      break;
    case 'waterproof-flooring':
      oldSlug = '|all-waterproof-flooring';
      slug = 'waterproof';
      zone = BroadstreetZoneType.Waterproof;
      break;
    default:
      oldSlug = '';
      slug = '';
      zone = BroadstreetZoneType.Default;
      break;
  }

  return {
    slug: hasOldSteup ? oldSlug : slug,
    zone: hasOldSteup ? BroadstreetZoneType.Default : zone,
  };
};

export const getPDPURL = (
  platform: 'MM' | 'DP',
  slug: string,
  sku: string,
  hash: string
) => {
  switch (platform) {
    case 'MM':
      return `${encodeURIComponent(slug)}-${encodeURIComponent(
        sku?.replace(/[^\-_\w]/g, '')
      )}${hash ? `#${hash}` : ''}`.toLowerCase();

    default:
      return `/d/${encodeURIComponent(slug)}/${encodeURIComponent(
        sku?.replace(/[^\-_\w]/g, '')
      )}${hash ? `#${hash}` : ''}`.toLowerCase();
  }
};

export const getTagType = (tag: string, useCic: boolean = false) => {
  const tagTitleMap: {
    [key: string]: string;
  } = {
    Whats_New: 'New',
    Sale: 'On Sale',
    Special_Order: 'Special',
    Owners_Favorite: 'Favorite',
  };
  // Create simiar array for array items -> Clearance, Sale, Special
  const priceArray = useCic
    ? ['OnSale', 'OnClearance', 'SpecialOrder']
    : ['Clearance', 'On_Sale', 'Sale', 'Special_Order'];
  const specialArray = useCic
    ? ['BestSeller', 'OwnersFavorite', 'SampleInStore', 'Featured']
    : ['Best_Seller', 'Owners_Favorite', 'Sample_In_Store'];
  const slug = slugify(tag, { lower: true });
  const label = tagTitleMap[tag] ?? toTitleCase(tag);

  if (specialArray.includes(tag)) {
    return {
      type: 'special',
      slug,
      label,
    };
  }
  if (priceArray.includes(tag)) {
    return {
      type: 'price',
      slug,
      label,
    };
  }
  return {
    type: 'info',
    slug,
    label,
  };
};

/**
 * Creates a link to a parent menu item, to be shown among its children.
 *
 * @param item The parent menu item.
 * @returns The parent as a leaf item, or null on mobile when the parent has
 * no link of its own.
 */
export const getNavParentAsChild = ({
  item,
  mode = 'desktop',
}: {
  item: ClientMenuItem;
  mode: 'mobile' | 'desktop';
}): ClientMenuItem | null => {
  if (mode === 'mobile' && !item.alias) return null;
  return { name: item.name, alias: item.alias, target: item.target };
};

/**
 * Trims navigation items down to what client menu components render.
 *
 * Items that are hidden (`hideInMenu`) or fail their visibility rules are
 * dropped together with their subtree, matching how the menus render them.
 * When `entrypoint` matches the position of a top-level item with visible
 * children, only those children are returned; the entrypoint item itself may
 * be hidden.
 */
export const toClientMenuItems = (
  items: NavigationItem[] | undefined,
  opts: { entrypoint?: string | number } = {}
): ClientMenuItem[] => {
  if (!items) return [];

  const entrypoint = Number(opts.entrypoint);
  if (entrypoint) {
    const entry = items.find(
      ({ position, hasVisibleChildren }) =>
        position === entrypoint && hasVisibleChildren
    );
    if (entry) return toClientMenuItems(entry.children);
  }

  const result: ClientMenuItem[] = [];
  for (const item of items) {
    if (item.hideInMenu || !item.isVisible) continue;

    const clientItem: ClientMenuItem = {
      name: item.name ?? '',
      alias: item.alias ?? '',
    };
    if (item.target) clientItem.target = item.target;
    const children = toClientMenuItems(item.children);
    if (children.length) clientItem.children = children;
    result.push(clientItem);
  }
  return result;
};

export const sleep = (timeout: number) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return new Promise((resolve, _reject) => {
    setTimeout(() => resolve(null), timeout);
  });
};

export const formatPhoneNumber = (value: string) => {
  let finalStr = '';

  if (value.length > 3) {
    finalStr += `(${value.slice(0, 3)}) `;
    if (value.length > 3 && value.length >= 6) {
      finalStr += `${value.slice(3, 6)}`;
      if (value.length > 6 && value.length <= 10) {
        finalStr += `-${value.slice(6, 10)}`;
      }
    } else {
      finalStr += value.slice(3, 6);
    }
  } else {
    finalStr += value;
  }
  return finalStr;
};

/**
 * Get the text contrast based on the background color provided
 */
export const getContrastYIQ = (hexcolor: string) => {
  const r = Number.parseInt(hexcolor.substring(1, 3), 16);
  const g = Number.parseInt(hexcolor.substring(3, 5), 16);
  const b = Number.parseInt(hexcolor.substring(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? 'black' : 'white';
};

export function decodeHtmlEntities(str: string) {
  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/<br>/g, ' ');
}

/**
 * * Extracts traffic data from URL search parameters.
 *
 * @param {URLSearchParams} params - The URLSearchParams object containing potential UTM parameters.
 * @returns {Object} An object containing the extracted traffic data, including source, medium,
 * campaign, content, term, and the type of traffic (UTM or direct).
 */
export const getTrafficDataFromSearchParams = (params: URLSearchParams) => ({
  Source: params?.get('utm_source') ?? 'direct',
  Medium: params?.get('utm_medium') ?? 'none',
  Campaign: params?.get('utm_campaign') ?? '(none)',
  Content: params?.get('utm_content') ?? '(none)',
  Term: params?.get('utm_term') ?? '(none)',
  Type: params?.has('utm_source') ? 'utm' : 'direct',
});

/**
 * Encode a filters object into a URLSearchParams
 * @param currentSearchParams the current window's search params (optional)
 * @param filters the filters to encode
 * @returns a URLSearchParams object with the encoded filters
 */
export function EncodeFiltersToSearchParams(
  currentSearchParams: URLSearchParams | null,
  filters: Filter[]
): URLSearchParams {
  const newSearchParams = new URLSearchParams(currentSearchParams ?? '');
  for (const filter of filters) {
    const name = slugify(filter.urlFriendlyName ?? filter.name, {
      lower: true,
    });
    // Delete any filters that don't have enabled values
    if (filter.enabledValues.length === 0) {
      newSearchParams.delete(name);
      continue;
    }
    const value = filter.enabledValues
      // Encode commas as full-width commas to not interfere with multiple query value parsing
      ?.map((v) => v.replaceAll(',', '，'))
      ?.join(',')
      ?.toLowerCase();
    newSearchParams.set(
      name,
      name === 'type' ? value.replaceAll(' ', '-') : value
    );
  }
  // Clear page on new filter selection to prevent 404s
  newSearchParams.delete('page');
  return newSearchParams;
}

export function sortWithPriority<T>(
  arr: T[],
  priorityMap: Record<string, number>,
  selector: (item: T) => string
): T[] {
  return [...arr].sort((a, b) => {
    const aKey = selector(a);
    const bKey = selector(b);

    const aPriority = priorityMap[aKey];
    const bPriority = priorityMap[bKey];

    const aHasPriority = aPriority !== undefined;
    const bHasPriority = bPriority !== undefined;

    if (aHasPriority && bHasPriority) {
      // Higher number = higher priority → comes first
      return bPriority - aPriority;
    }
    if (aHasPriority) {
      return -1;
    }
    if (bHasPriority) {
      return 1;
    }

    // fallback to alphabetical order
    return aKey.localeCompare(bKey);
  });
}

/**
 * Decodes an item code into slug and SKU components.
 * Expects item codes in the format: [slug]-[product-type]-[sku]
 *
 * @param itemCode - The item code to decode
 * @returns Object containing the slug and sku
 */
export const decodeItemCode = (
  itemCode: string
): { slug: string; sku: string } => {
  // List of known product types
  const possibleProductTypes = [
    'cork',
    'hardwood',
    'carpet',
    'carpet-tile',
    'luxury-vinyl',
    'tile',
    'laminate',
    'rug',
    'natural-stone',
    'vinyl',
  ];

  // Find the rightmost (last) product type separator in the string
  // Prioritize longer product types by checking them first
  let productType = '';
  let lastIndex = -1;

  // Sort by length descending to check longer patterns first
  const sortedTypes = [...possibleProductTypes].sort(
    (a, b) => b.length - a.length
  );

  for (const pt of sortedTypes) {
    const pattern = `-${pt}-`;
    const index = itemCode.lastIndexOf(pattern);
    if (index !== -1 && index > lastIndex) {
      lastIndex = index;
      productType = pt;
    }
  }

  if (!productType || lastIndex === -1) {
    console.warn('No known product type found in string', itemCode);
    return { slug: itemCode, sku: 'missing-sku' };
  }

  // SKU is everything after the last occurrence of the product type
  const sku = itemCode.slice(lastIndex + productType.length + 2); // +2 for surrounding dashes
  // Slug includes everything up to and including the product type
  const slug = itemCode.slice(0, lastIndex + productType.length + 1); // +1 for first dash

  return { slug, sku };
};
